"use client"

import { useState, useEffect } from "react"
import { Link, useLocation } from "react-router-dom"
import { Menu, X } from "lucide-react"

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [noPattern, setNoPattern] = useState(() => {
    if (typeof document !== "undefined") {
      return document.body.classList.contains("no-pattern")
    }
    return false
  })

  const location = useLocation()

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  // Keep noPattern in sync with body classes
  useEffect(() => {
    const updateFlag = () =>
      setNoPattern(document.body.classList.contains("no-pattern"))
    updateFlag()
    const observer = new MutationObserver(updateFlag)
    observer.observe(document.body, { attributes: true, attributeFilter: ["class"] })
    return () => observer.disconnect()
  }, [])

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 10)
    handleScroll()
    window.addEventListener("scroll", handleScroll, { passive: true })
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Active link helper
  const isActive = (path) => location.pathname === path

  // Special pages where navbar is always solid
  const isSpecialPage =
    location.pathname === "/admin" ||
    location.pathname === "/check-in" ||
    location.pathname === "/payment-confirmation" ||
    location.pathname === "/TravelBookings" ||
    location.pathname === "/quote" ||
    location.pathname === "/gallery"

  // Navbar background logic
  const navbarBackground = () => {
  // Always solid on special pages or noPattern pages (like Gallery)
  if (isSpecialPage || noPattern) {
    return "bg-primary-navy shadow-lg"
  }
  // Normal pages: transparent at top, gradient on scroll
  return scrolled 
    ? "bg-gradient-to-r from-primary-navy to-primary-green shadow-lg" 
    : "bg-transparent"
}

  return (
    <nav
      className={`fixed left-0 w-full z-50 transition-all duration-300 py-2 ${navbarBackground()}`}
      style={{
        backdropFilter: scrolled && !isSpecialPage && !noPattern ? "blur(8px)" : "none",
        WebkitBackdropFilter: scrolled && !isSpecialPage && !noPattern ? "blur(8px)" : "none",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <img
              src="/logos/AMC_White-8.png"
              alt="Africa Maritime Talent Logo"
              className="h-16 w-auto transition-all duration-300 hover:opacity-90"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-center space-x-8 mx-auto">
            {[
              { name: "Home", path: "/" },
              { name: "About", path: "/about-us" },
              { name: "Gallery", path: "/gallery" },
              { name: "Contact", path: "/contact-us" },
              { name: "Companies", path: "/confirmed-companies" },
              { name: "Request a Quote", path: "/quote" },
            ].map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`px-3 py-2 text-sm font-medium uppercase tracking-wider transition-colors ${
                  isActive(item.path)
                    ? "text-primary-orange"
                    : "text-white hover:text-primary-orange"
                }`}
              >
                {item.name}
              </Link>
            ))}
          </div>

          {/* Desktop Register Button */}
          

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="inline-flex items-center justify-center p-2 rounded-md text-white hover:text-[#2C3E50] hover:bg-white/10 focus:outline-none transition-colors"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              {isMenuOpen ? (
                <X className="block h-6 w-6" aria-hidden="true" />
              ) : (
                <Menu className="block h-6 w-6" aria-hidden="true" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden transition-all duration-300 ease-in-out ${
          isMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0 overflow-hidden"
        }`}
      >
        <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gradient-to-b from-primary-navy to-primary-green">
          {[
            { name: "Home", path: "/" },
            { name: "About Us", path: "/about-us" },
            { name: "Contact Us", path: "/contact-us" },
            { name: "Companies", path: "/confirmed-companies" },
            { name: "Request a Quote", path: "/quote" },
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`block px-3 py-2 rounded-md text-base font-medium ${
                isActive(item.path)
                  ? "text-primary-orange bg-white/10"
                  : "text-white hover:bg-white/10 hover:text-primary-orange"
              }`}
              onClick={() => setIsMenuOpen(false)}
            >
              {item.name}
            </Link>
          ))}
          
        </div>
      </div>
    </nav>
  )
}

export default Navbar
